import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Row, Col, Input, message, Table, Space, Tooltip, Popconfirm, Select, Tag, Statistic } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, SearchOutlined, EnvironmentOutlined, ReloadOutlined } from '@ant-design/icons';
import { usersService, type ApiUser } from '../services/usersService';
import { techniciansService } from '../services/techniciansService';
import socketService from '../services/socketService';
import TechnicianMap from '../components/TechnicianMap';
import UserAvatar from '../components/Common/UserAvatar';
import type { ColumnsType } from 'antd/es/table';
import { useAuth } from '../contexts/AuthContext';

const { Option } = Select;

const TechniciansPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isAgent = currentUser?.role === 'agent';
  const [loading, setLoading] = useState(false);
  const [technicians, setTechnicians] = useState<ApiUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [focusedUserId, setFocusedUserId] = useState<number | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchTechnicians = async () => {
    try {
      setLoading(true);
      const data = await usersService.getUsers({
        page: pagination.current,
        limit: pagination.pageSize,
        role: 'technician',
        search: searchTerm,
      });
      setTechnicians(data.data.users || []);
      setPagination(prev => ({
        ...prev,
        total: data.data.total || 0,
      }));
    } catch (error) {
      message.error('Erreur lors du chargement des techniciens');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTechnicians();
    // eslint-disable-next-line
  }, [pagination.current, pagination.pageSize, searchTerm]);

  // 🔌 Écoute Socket.io en temps réel des changements de statut de disponibilité
  useEffect(() => {
    const handleStatusChanged = (data: { user_id: number; availability_status: string }) => {
      console.log('⚡ [TechniciansPage] Changement de disponibilité en temps réel:', data);
      const targetId = Number(data.user_id);
      setTechnicians(prev =>
        prev.map(t => (Number(t.id) === targetId ? {
          ...t,
          availability_status: data.availability_status,
          technicianProfile: (t as any).technicianProfile ? {
            ...(t as any).technicianProfile,
            availability_status: data.availability_status
          } : undefined
        } : t))
      );
    };

    socketService.on('technician_status_changed', handleStatusChanged);
    return () => {
      socketService.off('technician_status_changed', handleStatusChanged);
    };
  }, []);

  const handleAddTechnician = () => {
    navigate('/users/new?role=technician');
  };

  const handleEditTechnician = (id: number) => {
    navigate(`/users/edit/${id}`);
  };

  const handleDeleteTechnician = async (id: number) => {
    try {
      await usersService.deleteUser(id);
      message.success('Technicien supprimé avec succès');
      fetchTechnicians();
    } catch (error) {
      message.error('Suppression échouée');
    }
  };

  const handleViewTechnician = (id: number) => {
    navigate(`/users/${id}`);
  };

  const handleLocateTechnician = (id: number) => {
    setFocusedUserId(id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    message.info('Localisation sur la carte...');
  };

  const handleUpdateAvailability = async (id: number, newStatus: string) => {
    try {
      await techniciansService.updateAvailabilityStatus(id, newStatus);
      message.success('Statut de disponibilité mis à jour avec succès');
      setTechnicians(prev =>
        prev.map(t => (t.id === id ? { ...t, availability_status: newStatus } : t))
      );
    } catch (error) {
      message.error('Échec de la mise à jour de la disponibilité');
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setAvailabilityFilter('');
    setStatusFilter('');
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Filtrage côté client pour la disponibilité et le statut de compte
  const filteredTechnicians = technicians.filter(tech => {
    const avail = tech.availability_status || 'offline';
    if (availabilityFilter && avail !== availabilityFilter) return false;

    const st = tech.status || 'active';
    if (statusFilter && st !== statusFilter) return false;

    return true;
  });

  // Statistiques
  const stats = [
    { 
      title: 'Total Techniciens', 
      value: pagination.total,
      color: '#1890ff'
    },
    { 
      title: 'En ligne (Disponibles)', 
      value: technicians.filter(t => t.availability_status === 'available').length,
      color: '#52c41a'
    },
    { 
      title: 'En intervention', 
      value: technicians.filter(t => t.availability_status === 'busy').length,
      color: '#faad14'
    },
    { 
      title: 'Hors ligne', 
      value: technicians.filter(t => !t.availability_status || t.availability_status === 'offline').length,
      color: '#8c8c8c'
    },
  ];

  const columns: ColumnsType<ApiUser> = [
    {
      title: 'Avatar',
      key: 'avatar',
      width: 70,
      align: 'center',
      render: (_, record) => {
        const firstName = record.first_name || record.firstName || '';
        const lastName = record.last_name || record.lastName || '';
        return (
          <UserAvatar
            src={record.profile_image}
            firstName={firstName}
            lastName={lastName}
            size={40}
          />
        );
      },
    },
    {
      title: 'Nom complet',
      key: 'name',
      render: (_, record) => {
        const firstName = record.first_name || record.firstName || '';
        const lastName = record.last_name || record.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim() || 'N/A';
        return (
          <Button 
            type="link" 
            onClick={() => handleViewTechnician(record.id)}
            style={{ padding: 0, fontWeight: 600, fontSize: '14px', textAlign: 'left', height: 'auto' }}
          >
            {fullName}
          </Button>
        );
      },
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => {
        const phone = record.phone || (record as any).phoneNumber || '';
        const email = record.email || '';
        return (
          <div>
            {phone && (
              <div style={{ fontWeight: 500, color: '#111827' }}>
                📱 {phone}
              </div>
            )}
            {email && (
              <div style={{ color: '#6b7280', fontSize: '12px', marginTop: 2 }}>
                ✉️ {email}
              </div>
            )}
            {!phone && !email && <span style={{ color: '#9ca3af' }}>N/A</span>}
          </div>
        );
      },
    },
    {
      title: 'Disponibilité (En ligne)',
      dataIndex: 'availability_status',
      key: 'availability_status',
      width: 200,
      render: (status: string | undefined, record: ApiUser) => {
        const currentStatus = status || 'offline';
        if (isAgent) {
          if (currentStatus === 'available') return <Tag color="green">🟢 En ligne (Disponible)</Tag>;
          if (currentStatus === 'busy') return <Tag color="gold">🟡 En intervention</Tag>;
          return <Tag color="default">⚪ Hors ligne</Tag>;
        }
        return (
          <Select
            value={currentStatus}
            style={{ width: 185 }}
            onChange={(val) => handleUpdateAvailability(record.id, val)}
            options={[
              {
                value: 'available',
                label: <Tag color="green">🟢 En ligne (Disponible)</Tag>,
              },
              {
                value: 'busy',
                label: <Tag color="gold">🟡 En intervention</Tag>,
              },
              {
                value: 'offline',
                label: <Tag color="default">⚪ Hors ligne</Tag>,
              },
            ]}
          />
        );
      },
    },
    {
      title: 'Compte',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status: string) => {
        const isActive = status === 'active';
        return (
          <Tag color={isActive ? 'green' : 'red'}>
            {isActive ? 'Actif' : 'Inactif'}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 170,
      align: 'center',
      render: (_: any, record: ApiUser) => (
        <Space size="small">
          <Tooltip title="Localiser sur la carte">
            <Button 
              type="text"
              icon={<EnvironmentOutlined />} 
              size="small"
              onClick={() => handleLocateTechnician(record.id)} 
            />
          </Tooltip>
          <Tooltip title="Voir les détails">
            <Button 
              type="text"
              icon={<EyeOutlined />} 
              size="small"
              onClick={() => handleViewTechnician(record.id)} 
            />
          </Tooltip>
          {!isAgent && (
            <>
              <Tooltip title="Modifier">
                <Button 
                  type="text"
                  icon={<EditOutlined />} 
                  size="small"
                  onClick={() => handleEditTechnician(record.id)} 
                />
              </Tooltip>
              <Popconfirm title="Supprimer ce technicien ?" onConfirm={() => handleDeleteTechnician(record.id)} okText="Oui" cancelText="Non">
                <Tooltip title="Supprimer">
                  <Button 
                    type="text"
                    icon={<DeleteOutlined />} 
                    size="small"
                    danger 
                  />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* En-tête — Masqué pour les agents selon acces_dashboard.md */}
      {!isAgent && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '24px',
          backgroundColor: '#fff',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>Gestion des Techniciens</h1>
            <p style={{ margin: '4px 0 0 0', color: '#666' }}>
              Gérez vos techniciens et leurs informations
            </p>
          </div>
          <Space>
            <Button 
              icon={<ReloadOutlined />} 
              size="large"
              onClick={fetchTechnicians}
              loading={loading}
              style={{ height: '40px' }}
            >
              Actualiser
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              size="large"
              onClick={handleAddTechnician}
              style={{ height: '40px' }}
            >
              Nouveau Technicien
            </Button>
          </Space>
        </div>
      )}

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} md={6} key={index}>
            <Card size="small" style={{ borderRadius: '8px' }}>
              <Statistic
                title={stat.title}
                value={stat.value}
                valueStyle={{ 
                  color: stat.color,
                  fontSize: '24px',
                  fontWeight: 600
                }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filtres */}
      <Card style={{ marginBottom: '24px', borderRadius: '8px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Input
              placeholder="Rechercher par nom, email, téléphone..."
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
              size="large"
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="Filtrer par disponibilité"
              style={{ width: '100%' }}
              size="large"
              allowClear
              value={availabilityFilter || undefined}
              onChange={(val) => {
                setAvailabilityFilter(val || '');
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
            >
              <Option value="available">🟢 En ligne (Disponible)</Option>
              <Option value="busy">🟡 En intervention</Option>
              <Option value="offline">⚪ Hors ligne</Option>
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="Filtrer par statut compte"
              style={{ width: '100%' }}
              size="large"
              allowClear
              value={statusFilter || undefined}
              onChange={(val) => {
                setStatusFilter(val || '');
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
            >
              <Option value="active">Actif</Option>
              <Option value="inactive">Inactif</Option>
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <Space style={{ width: '100%' }}>
              <Button 
                onClick={handleResetFilters}
                size="large"
                style={{ height: '40px' }}
              >
                Réinitialiser
              </Button>
              {isAgent && (
                <Tooltip title="Actualiser la liste">
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={fetchTechnicians}
                    loading={loading}
                    size="large"
                    style={{ height: '40px' }}
                  />
                </Tooltip>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Carte des techniciens */}
      <TechnicianMap focusedUserId={focusedUserId} />

      {/* Tableau */}
      <Card style={{ borderRadius: '8px', marginTop: '24px' }}>
        <Table
          columns={columns}
          dataSource={filteredTechnicians}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} techniciens`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({
                ...prev,
                current: page,
                pageSize: pageSize || 10,
              }));
            },
          }}
          scroll={{ x: 900 }}
        />
      </Card>
    </div>
  );
};

export default TechniciansPage;

